<template>
  <BottomModal :visible="visible" @close="$emit('close')" :fullHeight="false">
    <template #default>
      <div class="eam-body">

        <!-- Header -->
        <div class="eam-header">
          <div class="eam-header__thumb">
            <img
              v-if="event?.image"
              :src="event.image"
              class="eam-header__thumb-img"
              alt=""
            />
            <div v-else class="eam-header__thumb-fallback">
              <i class="fas fa-gift"></i>
            </div>
          </div>
          <div class="eam-header__info">
            <h4 class="eam-header__title">{{ event?.name }}</h4>
            <div class="eam-header__badges">
              <span v-if="event?.is_premium" class="eam-badge eam-badge--premium">
                <i class="fas fa-star"></i> Premium
              </span>
              <router-link
                v-else
                :to="`/event/${event?.id}?customize=1`"
                class="eam-badge eam-badge--customize"
                @click="$emit('close')"
              >
                <i class="fas fa-palette"></i> Personalizar evento
              </router-link>
              <span v-if="isDraft" class="eam-badge eam-badge--draft">Rascunho</span>
            </div>
          </div>
        </div>

        <!-- Draft: missing profile note -->
        <div v-if="isDraft && missingProfile" class="eam-note" role="status" aria-live="polite">
          <i class="fas fa-circle-exclamation eam-note__icon" aria-hidden="true"></i>
          <p class="eam-note__text mb-0">{{ draftActivationMessage }}</p>
        </div>

        <!-- Action rows -->
        <div class="eam-actions">
          <router-link
            :to="`/my-account/events/${event?.id}/edit`"
            class="eam-action-row eam-action-row--edit"
            @click="$emit('close')"
          >
            <div class="eam-action-row__icon-wrap eam-action-row__icon-wrap--edit">
              <i class="fas fa-pen-to-square"></i>
            </div>
            <div class="eam-action-row__content">
              <span class="eam-action-row__label">Editar evento</span>
              <span class="eam-action-row__sub">Altere nome, presentes e configurações</span>
            </div>
            <i class="fas fa-chevron-right eam-action-row__arrow"></i>
          </router-link>

          <div class="eam-actions__divider"></div>

          <router-link
            :to="`/event/${event?.id}`"
            class="eam-action-row eam-action-row--view"
            @click="$emit('close')"
          >
            <div class="eam-action-row__icon-wrap eam-action-row__icon-wrap--view">
              <i class="fas fa-eye"></i>
            </div>
            <div class="eam-action-row__content">
              <span class="eam-action-row__label">Ver evento</span>
              <span class="eam-action-row__sub">Como seus convidados estão vendo</span>
            </div>
            <i class="fas fa-chevron-right eam-action-row__arrow"></i>
          </router-link>
        </div>

        <!-- Share section -->
        <div class="eam-share">
          <p class="eam-share__label">Compartilhar com convidados</p>
          <div class="eam-share__row">
            <a
              :href="whatsappShareUrl"
              target="_blank"
              class="eam-share-pill eam-share-pill--whatsapp"
              aria-label="WhatsApp"
              @click.prevent="handleShareClick(whatsappShareUrl)"
            >
              <i class="fab fa-whatsapp"></i>
              <span>WhatsApp</span>
            </a>
            <a
              :href="facebookShareUrl"
              target="_blank"
              class="eam-share-pill eam-share-pill--facebook"
              aria-label="Facebook"
              @click.prevent="handleShareClick(facebookShareUrl)"
            >
              <i class="fab fa-facebook-f"></i>
              <span>Facebook</span>
            </a>
            <button
              class="eam-share-pill eam-share-pill--copy"
              aria-label="Copiar link"
              @click="handleCopyLink"
            >
              <i class="fas fa-link"></i>
              <span>Copiar</span>
            </button>
          </div>
        </div>

        <!-- Concluir cadastro (draft + missing profile) -->
        <div v-if="isDraft && missingProfile" class="mt-3">
          <router-link
            to="/my-account/profile?ref=createaccount"
            class="btn btn-color-site w-100"
            @click="$emit('close')"
          >
            Concluir cadastro
          </router-link>
        </div>

        <!-- Encerrar evento (danger text link) -->
        <div v-if="!isDraft && showFinalize" class="eam-danger">
          <button class="eam-danger__btn" @click="$emit('finalize')">
            <i class="fas fa-circle-xmark me-1"></i> Encerrar evento
          </button>
        </div>

      </div>
    </template>
  </BottomModal>
</template>

<script setup>
import { computed } from 'vue';
import { toast } from 'vue3-toastify';
import BottomModal from '@/components/BottomModal.vue';

const props = defineProps({
  visible: Boolean,
  event: Object,
  isDraft: Boolean,
  missingProfile: Boolean,
  draftActivationMessage: String,
  showFinalize: Boolean,
});

const emit = defineEmits(['close', 'finalize', 'after-share']);

const eventUrl = computed(() =>
  props.event ? `${window.location.origin}/event/${props.event.id}` : ''
);

const eventType = computed(() => props.event?.event_type || 'evento especial');
const eventName = computed(() => props.event?.baby_name || '');

const shareMessage = computed(() => {
  const tipo = eventType.value;
  const nome = eventName.value;
  const url = eventUrl.value;
  const headline = nome
    ? `🎁 Você foi convidado para o ${tipo} de ${nome}!`
    : `🎁 Você foi convidado para um ${tipo}!`;
  return `${headline}\n\nVeja a lista de presentes e escolha o seu presente para tornar esse momento inesquecível:\n${url}`;
});

const whatsappShareUrl = computed(() =>
  `https://wa.me/?text=${encodeURIComponent(shareMessage.value)}`
);

const facebookShareUrl = computed(() =>
  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl.value)}`
);

const twitterShareUrl = computed(() => {
  const tipo = eventType.value;
  const nome = eventName.value;
  const text = nome
    ? `🎁 Você foi convidado para o ${tipo} de ${nome}! Veja a lista de presentes:`
    : `🎁 Você foi convidado para um ${tipo} especial! Veja a lista de presentes:`;
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(eventUrl.value)}`;
});

const emailShareUrl = computed(() => {
  const tipo = eventType.value;
  const nome = eventName.value;
  const subject = nome
    ? `Você foi convidado para o ${tipo} de ${nome}! 🎁`
    : `Você foi convidado para um ${tipo} especial! 🎁`;
  const body = nome
    ? `Oi! 🎁\n\nVocê foi convidado para o ${tipo} de ${nome}.\n\nVeja a lista de presentes e escolha o seu:\n${eventUrl.value}`
    : `Oi! 🎁\n\nVocê foi convidado para um ${tipo} especial.\n\nVeja a lista de presentes e escolha o seu:\n${eventUrl.value}`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
});

function openDestination(url) {
  if (!url) return;
  if (url.startsWith('mailto:')) {
    window.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

function handleShareClick(url) {
  emit('close');
  openDestination(url);
  emit('after-share');
}

async function handleCopyLink() {
  const url = eventUrl.value;
  let copied = false;

  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch {
      // fall through to fallback
    }
  }

  if (!copied) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      copied = document.execCommand('copy');
      document.body.removeChild(textarea);
    } catch {
      // ignore
    }
  }

  if (copied) {
    toast.success('Link da lista copiado!');
  } else {
    toast.error('Não foi possível copiar. Tente segurar o link para copiar manualmente.');
  }
  emit('close');
  emit('after-share');
}
</script>

<style scoped>
/* ── Body ───────────────────────────────────────────────── */
.eam-body {
  padding: 0.5rem 0.75rem 1.25rem;
}

/* ── Header ─────────────────────────────────────────────── */
.eam-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0.75rem 0 1.25rem;
}

.eam-header__thumb {
  width: 60px;
  height: 60px;
  border-radius: 14px;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, #fff0f8 0%, #ffd6ec 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.eam-header__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.eam-header__thumb-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #ff50a3;
}

.eam-header__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.eam-header__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.eam-header__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 4px;
}

.eam-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-radius: 99px;
  padding: 2px 9px;
}

.eam-badge--premium {
  background: linear-gradient(135deg, #fff8dc, #ffeaa0);
  color: #7a5c00;
  border: 1px solid #e6c840;
}

.eam-badge--premium i {
  color: #c9920a;
  font-size: 0.6rem;
}

.eam-badge--free {
  background: #f4f4f4;
  color: #888;
  border: 1px solid #e0e0e0;
}

.eam-badge--customize {
  background: #f3f0ff;
  color: #6c3fc5;
  border: 1px solid #d4c8f7;
  text-decoration: none;
  transition: background 0.15s;
}

.eam-badge--customize:hover {
  background: #e8e2ff;
  color: #5a31b0;
}

.eam-badge--draft {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
}

/* ── Note (draft + missing profile) ────────────────────── */
.eam-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff8ea;
  border: 1px solid #ffe3b3;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 1rem;
}

.eam-note__icon {
  color: #d48806;
  font-size: 0.9rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.eam-note__text {
  color: #6f5b2a;
  font-size: 0.82rem;
  line-height: 1.4;
}

/* ── Action rows ────────────────────────────────────────── */
.eam-actions {
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 1.25rem;
}

.eam-actions__divider {
  height: 1px;
  background: #f4f4f4;
  margin: 0 16px;
}

.eam-action-row {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px 16px;
  text-decoration: none;
  color: #1a1a2e;
  cursor: pointer;
  transition: background 0.15s;
  background: #fff;
}

.eam-action-row:hover {
  background: #fafafa;
}

.eam-action-row__icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.9rem;
}

.eam-action-row__icon-wrap--edit {
  background: #fff0f8;
  color: #ff50a3;
}

.eam-action-row__icon-wrap--view {
  background: #eef2ff;
  color: #4f63d9;
}

.eam-action-row__content {
  flex: 1;
  min-width: 0;
}

.eam-action-row__label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.3;
}

.eam-action-row__sub {
  display: block;
  font-size: 0.72rem;
  color: #9e9e9e;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.eam-action-row__arrow {
  color: #ccc;
  font-size: 0.72rem;
  flex-shrink: 0;
}

/* ── Share section ──────────────────────────────────────── */
.eam-share {
  margin-bottom: 0.5rem;
}

.eam-share__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #b0b0b0;
  text-transform: uppercase;
  margin-bottom: 10px;
  text-align: center;
}

.eam-share__row {
  display: flex;
  gap: 8px;
}

.eam-share-pill {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 46px;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.15s;
  font-family: inherit;
}

.eam-share-pill:hover {
  opacity: 0.88;
  transform: translateY(-1px);
  color: #fff;
}

.eam-share-pill i {
  font-size: 1rem;
}

.eam-share-pill--whatsapp { background: #25D366; }
.eam-share-pill--facebook { background: #1877F2; }
.eam-share-pill--copy     { background: #32355d; }

/* ── Danger (encerrar) ──────────────────────────────────── */
.eam-danger {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f4f4f4;
}

.eam-danger__btn {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  font-family: inherit;
  transition: opacity 0.15s;
}

.eam-danger__btn:hover {
  opacity: 0.75;
}

/* ── Primary button (Concluir cadastro) ─────────────────── */
.btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
  padding: 0.85rem 1.5rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-color-site:hover {
  background-color: #e64593;
  border-color: #e64593;
  color: #fff;
}
</style>
